/**
 * @file AddElementMenu.tsx
 * @description «⊕ элемент» в колонке секции (phase19.16 Р2; uiux-design-r2
 * §4, §10; SB64): виды элементов с русскими названиями, значками и строкой
 * «для чего» (реестр `ELEMENT_KIND_META`). Колонка заполнена — кнопка
 * выключена с объяснением; «Подписка» — только при рассылке (SB26).
 *
 * Р2а (uiux-design-r2a §3, §6): виды — по контексту секции
 * (`SECTION_ELEMENT_CONTEXTS`). В шаблоне страницы товара первая группа —
 * «Данные товара» ((з) у обязательных); элемент, который в шаблоне уже
 * стоит, — серый «Уже есть в блоке «…»», нажатие показывает его. На обычных
 * страницах группы «Данные товара» нет.
 */

import { isProductElementKind, isProductRequiredKind, SECTION_ELEMENT_CONTEXTS, SITE_LIMITS, type SectionElementKind } from "@chaiberi/shared";
import { Lock, Plus } from "lucide-react";

import { Button } from "../../../ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from "../../../ui/dropdown-menu";
import { useSectionForm } from "../section-form-context";
import { ELEMENT_KIND_META } from "../../../../lib/site-builder/section-meta";
import { SHOP_FEATURES } from "../../../../lib/site-builder/shop-features";

export const COLUMN_FULL_TEXT = `В колонке ${SITE_LIMITS.elementsPerColumn} элементов — это максимум. Добавьте колонку или новый блок.`;

interface Props {
  full: boolean;
  onAdd: (kind: SectionElementKind) => void;
}

export function AddElementMenu({ full, onAdd }: Props) {
  const { context, taken, showElement } = useSectionForm();
  if (full) {
    return (
      <div className="flex flex-col gap-1">
        <Button variant="ghost" className="justify-start" disabled>
          <Plus aria-hidden />
          элемент
        </Button>
        <p className="type-caption text-on-surface-variant">{COLUMN_FULL_TEXT}</p>
      </div>
    );
  }
  const kinds = SECTION_ELEMENT_CONTEXTS[context];
  const product = kinds.filter(isProductElementKind);
  const general = kinds.filter((k) => !isProductElementKind(k));

  function item(kind: SectionElementKind) {
    const meta = ELEMENT_KIND_META[kind];
    const ref = isProductElementKind(kind) ? taken[kind] : undefined;
    const off = kind === "newsletter" && !SHOP_FEATURES.newsletter;
    const Icon = meta.icon;
    const hint = ref ? `Уже есть в блоке «${ref.blockTitle}» — показать` : off ? "Появится, когда у магазина заработает рассылка" : meta.hint;
    return (
      <DropdownMenuItem
        key={kind}
        disabled={off}
        onClick={() => (ref ? showElement(ref) : onAdd(kind))}
        className={ref ? "min-h-12 items-start gap-3 py-2 opacity-60" : "min-h-12 items-start gap-3 py-2"}
      >
        <Icon aria-hidden className="mt-0.5 size-4 shrink-0" />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="type-body font-medium text-on-surface">{meta.label}</span>
          <span className="type-caption text-on-surface-variant">{hint}</span>
        </span>
        {isProductRequiredKind(kind) ? <Lock aria-label="Обязательный элемент" className="mt-1 size-3.5 shrink-0 text-on-surface-variant" /> : null}
      </DropdownMenuItem>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={(props) => (
          <Button {...props} variant="ghost" className="justify-start text-primary">
            <Plus aria-hidden />
            элемент
          </Button>
        )}
      />
      <DropdownMenuContent align="start" className="max-h-[60dvh] w-80 max-w-[calc(100vw-2rem)] overflow-y-auto">
        {product.length > 0 ? (
          <>
            <DropdownMenuGroup>
              <DropdownMenuLabel>Данные товара</DropdownMenuLabel>
              {product.map(item)}
            </DropdownMenuGroup>
            <DropdownMenuGroup>
              <DropdownMenuLabel>Общие элементы</DropdownMenuLabel>
              {general.map(item)}
            </DropdownMenuGroup>
          </>
        ) : (
          general.map(item)
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
